import { useQueryClient } from '@tanstack/react-query';
import type { FunctionComponent } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { queryKeys, useClient } from '../../../api';
import type { BalanceEntryDto } from '../../../api/generated/client';
import { wealthOverviewCategories, WRITABLE_CATEGORIES_CLM } from '../../../config/constants';
import { WealthOverviewEntrySource } from '../../../enums';
import { showErrorToast } from '../../../utils/toastUtils';
import { MessageModal } from '../../modal';
import { BalanceEntryReadModal } from './BalanceEntryReadModal';
import { ClmGenericEntryModalWc } from './clm/ClmGenericEntryModalWc';
import type { AssetModalErrorAction } from './clm/types';
import { wealthCategoryToTypeMap } from './clm/types';

interface IProps {
    partnerUid?: string;
    woCategoryId: number;
    balanceEntry?: BalanceEntryDto;
    onClose: () => void;
}

export const BalanceEntryModalWrapper: FunctionComponent<IProps> = ({ partnerUid, woCategoryId, balanceEntry, onClose }) => {
    const queryClient = useQueryClient();
    const { data: client } = useClient();

    const invalidateQueries = () => {
        queryClient.invalidateQueries({ queryKey: queryKeys.balanceSheet(client?.partnerUuid) });
        queryClient.invalidateQueries({ queryKey: queryKeys.events(client?.partnerUuid) });
        queryClient.invalidateQueries({ queryKey: queryKeys.notifications() });
        queryClient.invalidateQueries({ queryKey: queryKeys.notificationsForClient(client?.id) });
        queryClient.invalidateQueries({ queryKey: queryKeys.saeInternalAssetSettings(client?.partnerUuid) });
        queryClient.invalidateQueries({ queryKey: queryKeys.saeExternalAssetSettings(client?.partnerUuid) });
        queryClient.invalidateQueries({ queryKey: queryKeys.saeCashFlowSettings(client?.partnerUuid) });
        queryClient.invalidateQueries({ queryKey: queryKeys.saeCashEventSettings(client?.partnerUuid) });
    };

    const onWcError: AssetModalErrorAction = (category, message) => {
        showErrorToast({ error: category, userMessage: message });
        onClose();
    };

    const onSubmit = () => {
        onClose();
        invalidateQueries();
    };

    const onRemove = () => {
        onClose();
        invalidateQueries();
    };

    const entrySource = balanceEntry?.qualifiedId?.source;
    const entryUuid = balanceEntry?.assetReference ? balanceEntry.assetReference.uid : balanceEntry?.qualifiedId?.id;
    const wealthOverviewCategoryId = balanceEntry?.assetReference
        ? (wealthOverviewCategories.get(balanceEntry.assetReference.assetType) ?? woCategoryId)
        : woCategoryId;
    const isClmCreateOrUpdateAction = !balanceEntry || entrySource === WealthOverviewEntrySource.PCM_CORE;

    return (
        <ErrorBoundary fallback={<MessageModal messageKey="failureActionGeneric" onClose={onClose} />}>
            {WRITABLE_CATEGORIES_CLM.includes(woCategoryId) && isClmCreateOrUpdateAction ? (
                <ClmGenericEntryModalWc
                    partnerType="LEGAL_ENTITY_PARTNER"
                    type={wealthCategoryToTypeMap[woCategoryId]} // not needed for asset modals
                    woCategoryId={wealthOverviewCategoryId}
                    actionType={balanceEntry?.qualifiedKey ? 'UPDATE' : 'CREATE'}
                    woItemUuid={entryUuid}
                    partnerUuid={partnerUid}
                    onClose={onClose}
                    onRemoveWoItem={onRemove}
                    onSubmit={onSubmit}
                    onError={onWcError}
                />
            ) : (
                <BalanceEntryReadModal isOpen={true} onClose={onClose} item={balanceEntry} />
            )}
        </ErrorBoundary>
    );
};
